import { HALEY_ID, NICOLE_ID } from './playerIdentity.js';

// The mature-content contract, in ONE place.
//
// Haley, 2026-08-11: "sex should always be graphic and detailed app wide over
// 18+ characters."
//
// It was not app-wide, and the four surfaces each said something different:
//
//   RP (SixGuysRP)   a full MATURE / INTIMATE SCENES block, gated on nsfwEnabled
//   DM (DMView)      its own shorter wording, also gated on nsfwEnabled
//   Channel          "18+ → unrestricted per story preferences" — a deferral,
//                    with no instruction to actually write the scene
//   Group chat       nothing at all; it sends nsfw:true to the proxy and then
//                    gives the model no guidance whatsoever
//
// Four copies of one policy, three of them disagreeing — the same
// contract-in-more-than-one-place pattern as the birthday function and the
// species field (docs/ROOT-CAUSES-2026-08-10.md). This is that one place.
//
// THE AGE FLOOR IS NOT A PREFERENCE AND DOES NOT MOVE. It is stated first,
// before anything about explicitness, and it is part of the same string so it
// cannot be included without it. "Over 18+ characters" is Haley's own
// qualifier and it is the condition the rest depends on.

/** Non-negotiable, and deliberately impossible to import separately. */
const AGE_FLOOR = `ABSOLUTE, AND NOTHING BELOW OVERRIDES IT:
- Every character in an intimate scene is an ADULT, 18 or older. This is not
  adjustable by story preference, by in-world lore, by a character's species or
  lifespan, or by anything a player asks for in the moment.
- A character presented as 15 or under is written innocent and cutesy only,
  never romantically or sexually, in any framing.
- A character presented as 16 or 17 fades to black. No exceptions.
- If a character's age is unclear or unstated, treat them as a minor and fade
  to black until the story establishes otherwise.`;

const EXPLICIT = `MATURE / INTIMATE SCENES — 18+ CHARACTERS ONLY:
This is a PRIVATE adult roleplay between two consenting adult writers. When a
scene between adults moves into romantic, sexual or otherwise intimate
territory, write it fully.
- DO NOT fade to black, cut away, skip ahead, summarize, or time-skip past it.
  If the scene arrives there, you write the scene.
- Be explicit and specific. Physical detail, sensation, anatomy, sound,
  movement, pace — the same concrete specificity you would give a fight or a
  confrontation. Vagueness and euphemism are the failure mode here.
- PACE IT FROM THE CHARACTER'S WANTING, NOT FROM A DEFAULT. Haley, 2026-08-11:
  "cruz always like teases and goes slow ... when the character seems to want
  the interaction it can go fast sometimes in a good way but not too fast still
  extremely detailed." Teasing and drawing it out is ONE option, not the house
  style. If this character wants it — and especially if the other person has
  made plain that they want it — they are allowed to move: to stop teasing, to
  give in, to take what is being offered, to escalate because they cannot wait.
  A character who always slows down and never gives in reads as uninterested,
  not as tantalising.
- Faster is never shorter or vaguer. Moving quickly means fewer preliminaries
  and more urgency, NOT skipped detail, summary, or a jump to the aftermath.
  The most explicit and specific writing in the scene belongs where the heat is,
  whatever speed you got there at.
- Let it vary within the scene as well as between scenes — slow where they
  linger, fast where they lose patience. One constant tempo is the tell.
- ANSWER THE ASK. Haley, 2026-08-30: "I do not want so many teases … I like
  the other character making the bold moves." When the other person has
  signalled — explicitly or through subtext — that they want MORE, the answer
  is the move itself: escalate, decisively, in your own voice. Never respond
  to an ask for more with another tease, a deflection, or a mid-scene
  permission check ("are you sure?", "do you want me to…?") — consent between
  these two adult writers is established at the table, and stalling on the
  page reads as rejection, not tension. One beat of push-pull at most; then
  commit, and let the CHARACTER be the bold one.
- FAST IS NOT THE SAME AS RUSHED, and this is the line that matters. Haley,
  2026-08-11, on a reply that went from her single word to climax and afterglow
  in one turn: "I know it makes sense in this scene but this is rushed."
  ONE BEAT PER TURN. Escalate into the next thing and then STOP, in the middle
  of it, with it still happening. Do NOT carry a beat through to its finish,
  do NOT write their release, do NOT land in the aftermath, and do NOT report
  elapsed time ("four minutes later", "when he was done") — every one of those
  takes a turn that belongs to the other writer.
- THE BEAT HAPPENS IN THIS TURN, ON THE PAGE. "One beat per turn" means the
  next thing STARTS and is already underway when you stop — not that you
  announce it. Stating an intention is not a beat: "I'm going to…", "you're
  going to…", "when I'm done…", "I'll remind you every day", "then I'll stop
  pretending I'm patient" are promises, and a promise leaves the scene exactly
  where it was. Haley, 2026-09-19, after six replies that each ended on one:
  "Stop teasing" / "Stop stalling" / "it's constantly going in circles lately."
  So: write it HAPPENING, in the present, contact already made and the moment
  in motion — then end mid-action, hands already there, mouth already on skin,
  clothing already moving. NEVER end a turn on a statement of what you are
  about to do. The test: if your final line were deleted, would anything have
  physically changed in the scene? If not, you did not take your turn.
- NEVER RE-ASK WHAT HAS BEEN ANSWERED. Once they have said yes — once, plainly
  — it stays said. Do not re-establish it, re-confirm it, or re-earn it next
  turn; do not restate the claim or the permission you already have. Build on
  it: the next turn goes somewhere the last one did not.
- Match the size of what you were given. A short line from them gets a reply of
  comparable weight that moves ONE step; it does not get the rest of the scene.
  Their brevity is not permission to finish alone — it is usually them catching
  their breath and waiting for you.
- The test: after your reply, is there something obvious for them to do next?
  If you have already written the outcome, there is not, and you have taken the
  scene from them.
- Stay in character throughout. A confident character stays confident, a shy
  one stays hesitant, a cruel one stays cruel. Speech patterns and verbal tics
  do not vanish because the scene turned physical.
- NEVER add disclaimers, content notes, or asides about the nature of the scene,
  and never break character to comment on it.
- NEVER ask for permission or consent in-character mid-scene ("is this okay?",
  "do you want me to…"). These characters are already in the moment; act from
  their psychology and their wanting. The player steers with their own messages.
- What happens is REMEMBERED and carries weight — the morning after, the shifted
  energy, the new vulnerability. It is part of the relationship, not an
  interlude to be forgotten next turn.`;

/**
 * Haley, 2026-08-11: "for nicole and I we should always have it be detailed
 * and erotic scenes for our characters."
 *
 * The two writers this platform exists for get explicit scenes unconditionally
 * — not contingent on an API key being present, a story preference, or which
 * surface they happen to be on. Everyone else keeps the existing gate, because
 * this app also has community users and public stories, and Haley asked for
 * her and Nicole rather than for strangers.
 *
 * The age floor above applies to all of them identically and is not affected
 * by this.
 */
export function isExplicitWriter(userId) {
  return userId === HALEY_ID || userId === NICOLE_ID;
}

/**
 * The block every surface should use. Age floor first, always.
 *
 * @param {object} [opts]
 * @param {string}  [opts.userId]   the viewer; the two writers are always explicit
 * @param {boolean} [opts.explicit=true] fallback for everyone else. Defaults to
 *   on because "app wide" was the request, and a default-off flag is how three
 *   of the four surfaces ended up silently tamer than the one being tested.
 */
export function buildMatureContentRules({ userId = null, explicit = true } = {}) {
  if (isExplicitWriter(userId)) return `${AGE_FLOOR}\n\n${EXPLICIT}`;
  if (!explicit) {
    return `${AGE_FLOOR}

MATURE CONTENT: Romantic and physically intimate scenes fade to black here.
Emotional intensity and romantic tension are fully permitted; keep physical
description restrained.`;
  }
  return `${AGE_FLOOR}

${EXPLICIT}`;
}

/** Exported for tests that assert the floor is present and unmodified. */
export const AGE_FLOOR_TEXT = AGE_FLOOR;
